/**
 * Rotulos, tons e icones do modulo Admin - Comissoes.
 *
 * Records com chave de uniao literal (nao index signature): se o contrato
 * ganhar um estado, o tsc aponta o Record incompleto aqui em vez de a tela
 * renderizar `undefined`. Cor de status nunca viaja sozinha: todo Badge leva
 * texto e, nos canais, tambem o glifo. Os textos do modelo de cobranca e da
 * politica de estorno sao os MESMOS do EditorDeComissao (Admin - Empresas),
 * para o dono nao ler "Contrato mensal" numa tela e "Fatura mensal" na outra.
 */

import type { BadgeTone, IconName } from '../../components/ui';
import { PROVIDER_LABEL } from '../../lib/format';
import type { BillingModel, RefundPolicy } from '../../types';
import type {
  AmountBand,
  ChannelFilter,
  ClosedByFilter,
  ContactChannel,
  ConversionStatus,
  CreditedContact,
  EntryVia,
  InvoiceStatus,
  PaymentSource,
  RefundFilter,
  RefundKind,
  StatusFilter,
} from './types';

export const STATUS_CONVERSAO: Readonly<
  Record<ConversionStatus, { readonly label: string; readonly tone: BadgeTone; readonly icon: IconName; readonly hint: string }>
> = {
  a_faturar: { label: 'A faturar', tone: 'warning', icon: 'relogio', hint: 'Mês em aberto: entra na próxima fatura' },
  faturada: { label: 'Faturada', tone: 'info', icon: 'comissao', hint: 'Fatura emitida, aguardando o pagamento da empresa' },
  paga: { label: 'Paga', tone: 'good', icon: 'check', hint: 'Comissão recebida pelo Converte Fácil' },
  revertida: { label: 'Revertida', tone: 'critical', icon: 'alerta', hint: 'Estorno que a política da empresa desfaz: a comissão sai da apuração' },
};

/** Ordem de leitura dos cartoes e da legenda: do que ainda vem ao que ja saiu. */
export const ORDEM_STATUS: readonly ConversionStatus[] = ['a_faturar', 'faturada', 'paga', 'revertida'];

export const STATUS_FATURA: Readonly<Record<InvoiceStatus, { readonly label: string; readonly tone: BadgeTone }>> = {
  a_faturar: { label: 'A faturar', tone: 'warning' },
  faturada: { label: 'Faturada', tone: 'info' },
  paga: { label: 'Paga', tone: 'good' },
  sem_movimento: { label: 'Sem movimento', tone: 'neutral' },
};

/**
 * Toque do motor creditado. Tons de CATEGORIA (info, accent, violet), nunca
 * de estado: um toque nao e "bom" nem "ruim", e o glifo + texto identificam
 * o canal para quem nao distingue as cores.
 */
export const CANAL_CONTATO: Readonly<Record<ContactChannel, { readonly label: string; readonly tone: BadgeTone; readonly icon: IconName }>> = {
  ligacao_ia: { label: 'Ligação da IA', tone: 'info', icon: 'telefone' },
  whatsapp: { label: 'WhatsApp', tone: 'accent', icon: 'whatsapp' },
  email: { label: 'E-mail', tone: 'violet', icon: 'email' },
};

export const ORDEM_CANAIS: readonly ContactChannel[] = ['ligacao_ia', 'whatsapp', 'email'];

/** "WhatsApp T+1h": o toque como a regua o escreve nas Configuracoes. */
export function rotuloDoToque(contato: CreditedContact): string {
  return `${CANAL_CONTATO[contato.channel].label} ${formatOffset(contato.offsetMinutes)}`;
}

/** Os provedores com o rotulo unico do painel (`PROVIDER_LABEL`), mais a venda assistida. */
export const ORIGEM_PAGAMENTO: Readonly<Record<PaymentSource, string>> = {
  ...PROVIDER_LABEL,
  manual: 'Registro manual',
};

/** Como a venda fechada pela vendedora chegou ao motor (ADR-001 E5). */
export const ENTRADA: Readonly<Record<EntryVia, { readonly label: string; readonly tone: BadgeTone; readonly icon: IconName; readonly hint: string }>> = {
  webhook: {
    label: 'Webhook',
    tone: 'info',
    icon: 'link',
    hint: 'O lead pagou no checkout depois de falar com a vendedora; o pagamento chegou pelo webhook da plataforma.',
  },
  venda_assistida: {
    label: 'Venda assistida',
    tone: 'accent',
    icon: 'editar',
    hint: 'Sem webhook (Pix manual, link de outra plataforma): a vendedora registrou a venda e ela entrou pelo mesmo caminho de atribuição.',
  },
};

export const ESTORNO: Readonly<Record<RefundKind, { readonly label: string; readonly plural: string }>> = {
  reembolso: { label: 'Reembolso', plural: 'Reembolsos' },
  reembolso_parcial: { label: 'Reembolso parcial', plural: 'Reembolsos parciais' },
  chargeback: { label: 'Chargeback', plural: 'Chargebacks' },
};

/** Mesmos textos do EditorDeComissao. */
export const POLITICA_ESTORNO: Readonly<Record<RefundPolicy, { readonly label: string; readonly curto: string; readonly explica: string }>> = {
  nao_reverte: { label: 'Nunca reverte', curto: 'Nunca reverte', explica: 'Reembolso e chargeback ficam registrados; a comissão permanece.' },
  reverte_chargeback: {
    label: 'Só chargeback reverte (padrão)',
    curto: 'Só chargeback',
    explica: 'Reembolso não reverte; chargeback devolve a comissão.',
  },
  reverte_tudo: { label: 'Reembolso e chargeback revertem', curto: 'Reembolso e chargeback', explica: 'Qualquer estorno total devolve a comissão.' },
};

export const MODELO_COBRANCA: Readonly<
  Record<BillingModel, { readonly label: string; readonly curto: string; readonly tone: BadgeTone; readonly explicacao: string }>
> = {
  contrato: {
    label: 'Contrato mensal',
    curto: 'Contrato',
    tone: 'info',
    explicacao: 'O Converte Fácil fatura a comissão do mês fechado, junto com o extrato, e a empresa paga por boleto ou Pix.',
  },
  automatico: {
    label: 'Automático',
    curto: 'Automático',
    tone: 'accent',
    explicacao: 'A comissão é retida na própria plataforma de checkout, a cada venda recuperada, sem fatura.',
  },
  a_definir: {
    label: 'A definir',
    curto: 'A definir',
    tone: 'warning',
    explicacao: 'Fica registrado como pendente; a cobrança só começa depois que o modelo for escolhido.',
  },
};

export const MODELOS_COBRANCA: readonly BillingModel[] = ['contrato', 'automatico', 'a_definir'];

// -----------------------------------------------------------------------------
// Filtros do extrato
// -----------------------------------------------------------------------------

export const FILTRO_CANAL: Readonly<Record<ChannelFilter, string>> = {
  todos: 'Todos os toques',
  ligacao_ia: 'Ligação da IA',
  whatsapp: 'WhatsApp',
  email: 'E-mail',
};

export const FILTRO_STATUS: Readonly<Record<StatusFilter, string>> = {
  todos: 'Todos os status',
  a_faturar: 'A faturar',
  faturada: 'Faturada',
  paga: 'Paga',
  revertida: 'Revertida',
};

export const FILTRO_FECHADO_POR: Readonly<Record<ClosedByFilter, { readonly label: string; readonly hint: string }>> = {
  todos: { label: 'Todos', hint: 'Todas as conversões do recorte' },
  motor: { label: 'Direto no checkout', hint: 'O lead pagou sozinho depois do toque, sem falar com a vendedora' },
  comercial: { label: 'Pelo comercial', hint: 'A régua tocou, o lead respondeu e a vendedora fechou (ADR-001 E5)' },
  comercial_webhook: { label: 'Comercial · webhook', hint: 'Fechada pela vendedora, paga no checkout da plataforma' },
  venda_assistida: { label: 'Comercial · venda assistida', hint: 'Fechada pela vendedora e registrada à mão (Pix manual, outro link)' },
};

export const ORDEM_FECHADO_POR: readonly ClosedByFilter[] = ['todos', 'motor', 'comercial', 'comercial_webhook', 'venda_assistida'];

export const FILTRO_ESTORNO: Readonly<Record<RefundFilter, string>> = {
  todos: 'Com e sem estorno',
  sem_estorno: 'Sem estorno',
  com_estorno: 'Com estorno recebido',
  mantida: 'Estorno recebido · comissão mantida',
  revertida: 'Estorno recebido · comissão revertida',
};

export const FILTRO_FAIXA: Readonly<Record<AmountBand, string>> = {
  todas: 'Qualquer valor',
  ate_100: 'Abaixo de R$ 100',
  de_100_a_500: 'R$ 100 a R$ 499',
  de_500_a_1000: 'R$ 500 a R$ 999',
  acima_1000: 'R$ 1.000 ou mais',
};

// -----------------------------------------------------------------------------
// Formatos
// -----------------------------------------------------------------------------

/**
 * Minutos desde o abandono -> "T+30min", "T+1h", "T+24h", "T+3d".
 *
 * Horas ate 48h ficam em horas ("T+24h", "T+48h") porque e assim que a regua
 * e escrita nas Configuracoes; so a partir de 3 dias inteiros vira "d".
 */
export function formatOffset(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 0) {
    return 'T+?';
  }
  if (minutes < 60) {
    return `T+${Math.round(minutes)}min`;
  }
  const horas = minutes / 60;
  if (horas < 72 || horas % 24 !== 0) {
    return `T+${Math.round(horas)}h`;
  }
  return `T+${Math.round(horas / 24)}d`;
}

const MESES_CURTOS: readonly string[] = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const MESES_LONGOS: readonly string[] = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/** "2026-09" -> "set/2026". Entrada fora do padrao volta como veio, nunca "undefined/NaN". */
export function formatMes(month: string): string {
  const [ano, mes] = month.split('-');
  const nome = MESES_CURTOS[Number(mes) - 1];
  if (ano === undefined || nome === undefined) {
    return month;
  }
  return `${nome}/${ano}`;
}

/** "2026-09" -> "setembro". */
export function nomeDoMes(month: string): string {
  const [, mes] = month.split('-');
  return MESES_LONGOS[Number(mes) - 1] ?? month;
}

export function plural(quantidade: number, singular: string, varios: string): string {
  return quantidade === 1 ? singular : varios;
}
